import {
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ReportGmailerrorred,
  TaskAlt,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { Fragment, memo, useState } from 'react';

import { diseaseLib } from 'entities/disease';

import { Country, DiseaseListItem, ETypePets, Pagination } from 'shared/api';
import { ProgressHelp } from 'shared/assets/svg';

import { formatedPetsTypeName } from '../../lib';

type Props = {
  list: DiseaseListItem[];
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  isPets?: boolean;
  loadingEditId?: string | null;
  loadingVaccineId?: string | null;
  countriesList?: Country[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string) => void;
  onEdit: (diseaseId: string) => void;
  onVaccineClick?: (vaccineId: string) => void;
  onAlertClick?: (diseaseId: string, countryCode: string) => void;
};

export const DiseaseTablePaginated = memo(
  ({
    list,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    isPets = false,
    loadingEditId = null,
    loadingVaccineId = null,
    countriesList,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onEdit,
    onVaccineClick,
    onAlertClick,
  }: Props) => {
    const handleChangePage = (event: unknown, newPage: number) => {
      onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleSortRequest = (columnId: string) => {
      onSortChange(columnId);
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 305px)' }}>
            <Table stickyHeader aria-label="disease table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell align="center">
                    <ProgressHelp />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest('name')}
                    >
                      Disease
                    </TableSortLabel>
                  </TableCell>
                  {isPets && <TableCell>Type</TableCell>}
                  <TableCell>Mandatory</TableCell>
                  <TableCell>Core</TableCell>
                  <TableCell>Additional</TableCell>
                  <TableCell>Epidemic</TableCell>
                  <TableCell>Unallocated</TableCell>
                  <TableCell width={460}>Vaccines</TableCell>
                  <TableCell>Controls</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length > 0 ? (
                  list.map((row) => (
                    <Row
                      key={row.id}
                      row={row}
                      isPets={isPets}
                      isLoadingEdit={loadingEditId === row.id}
                      loadingVaccineId={loadingVaccineId}
                      countriesList={countriesList}
                      onEdit={onEdit}
                      onVaccineClick={onVaccineClick}
                      onAlertClick={onAlertClick}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isPets ? 11 : 10} align="center">
                      No diseases found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

DiseaseTablePaginated.displayName = 'DiseaseTablePaginated';

type RowProps = {
  row: DiseaseListItem;
  isPets?: boolean;
  isLoadingEdit?: boolean;
  loadingVaccineId?: string | null;
  countriesList?: Country[];
  onEdit: (diseaseId: string) => void;
  onVaccineClick?: (vaccineId: string) => void;
  onAlertClick?: (diseaseId: string, countryCode: string) => void;
};

const Row = ({
  row,
  isPets = false,
  isLoadingEdit = false,
  loadingVaccineId = null,
  countriesList,
  onEdit,
  onVaccineClick,
  onAlertClick,
}: RowProps) => {
  const COUNT_VACCINES = 8;
  const [open, setOpen] = useState(false);

  return (
    <Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Tooltip
            title={(row.alerts?.length ?? 0) > 0 ? `${row.alerts.length} country alert(s)` : ''}
            arrow
            disableHoverListener={(row.alerts?.length ?? 0) === 0}
          >
            <Badge
              badgeContent={row.alerts?.length ?? 0}
              color="warning"
              overlap="circular"
              invisible={(row.alerts?.length ?? 0) === 0}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: 16,
                  minWidth: 16,
                  padding: '0 4px',
                  right: 4,
                  top: 4,
                },
              }}
            >
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
                sx={{ color: '#29b194', p: 0 }}
              >
                {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </Badge>
          </Tooltip>
        </TableCell>
        <TableCell align="center" sx={{ verticalAlign: 'top' }}>
          {row.verified ? <TaskAlt color="success" /> : <ReportGmailerrorred color="warning" />}
        </TableCell>
        <TableCell component="th" scope="row" sx={{ verticalAlign: 'top' }}>
          <Box mb={1}>{row.name}</Box>
          <Box color="rgba(116, 116, 116, 1)">{row.code}</Box>
        </TableCell>
        {isPets && (
          <TableCell sx={{ verticalAlign: 'top' }}>{formatedPetsTypeName(row.type_pets as ETypePets)}</TableCell>
        )}
        <TableCell sx={{ verticalAlign: 'top' }} align="center">
          {row.mandatory}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }} align="center">
          {row.basic}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }} align="center">
          {row.additional}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }} align="center">
          {row.epidemic}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }} align="center">
          {row.notDistributed}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Box display="flex" flexWrap="wrap">
            {row.vaccines.length === 0 ? (
              <Box
                component="span"
                p={0.5}
                m={0.25}
                sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', borderRadius: '4px' }}
              >
                No vaccines
              </Box>
            ) : (
              row.vaccines.slice(0, COUNT_VACCINES).map((vaccine) => {
                const isVaccineLoading = loadingVaccineId === vaccine.id;
                return (
                  <Tooltip key={vaccine.id} title={vaccine.name}>
                    <Box
                      component="span"
                      p={0.5}
                      m={0.25}
                      onClick={() => {
                        if (!isVaccineLoading) {
                          onVaccineClick?.(vaccine.id);
                        }
                      }}
                      sx={{
                        position: 'relative',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '4px',
                        color: vaccine.verified ? '#29b194' : 'inherit',
                        cursor: isVaccineLoading ? 'default' : onVaccineClick ? 'pointer' : 'default',
                        '&:hover':
                          onVaccineClick && !isVaccineLoading
                            ? {
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              }
                            : {},
                      }}
                    >
                      {isVaccineLoading && (
                        <CircularProgress
                          size={14}
                          sx={{
                            color: '#29b194',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-7px',
                            marginLeft: '-7px',
                          }}
                        />
                      )}
                      <Box component="span" sx={{ opacity: isVaccineLoading ? 0.3 : 1, transition: 'opacity 0.2s' }}>
                        {diseaseLib.formatedName(vaccine.name)}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })
            )}
            {row.vaccines.length > COUNT_VACCINES && (
              <Box
                component="span"
                p={0.5}
                m={0.25}
                sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', borderRadius: '4px' }}
              >
                + {row.vaccines.length - COUNT_VACCINES}
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <IconButton
            onClick={() => {
              if (!isLoadingEdit) {
                onEdit(row.id);
              }
            }}
            size="small"
            disabled={isLoadingEdit}
          >
            {isLoadingEdit ? (
              <CircularProgress size={20} sx={{ color: '#29b194' }} />
            ) : (
              <Edit sx={{ color: '#29b194' }} />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={isPets ? 11 : 10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {open && (
              <Box sx={{ margin: 1 }}>
                {(row.alerts?.length ?? 0) > 0 && (
                  <Box m={2} display="flex" flexWrap="wrap" alignItems="center" gap={0.5}>
                    <Box component="span" mr={0.5} sx={{ color: 'rgba(116, 116, 116, 1)' }}>
                      Alerts:
                    </Box>
                    {row.alerts.map((alert) => {
                      const countryName =
                        countriesList?.find((c) => c.code === alert.countryCode)?.name ?? alert.countryCode;
                      return (
                        <Tooltip key={alert.countryCode} title={alert.note} arrow>
                          <Chip
                            size="small"
                            icon={<WarningAmberOutlined sx={{ fontSize: 14 }} />}
                            label={countryName}
                            clickable={!!onAlertClick}
                            onClick={onAlertClick ? () => onAlertClick(row.id, alert.countryCode) : undefined}
                            sx={{
                              backgroundColor: 'rgba(237, 108, 2, 0.08)',
                              color: 'warning.dark',
                              border: '1px solid rgba(237, 108, 2, 0.3)',
                              '& .MuiChip-icon': { color: 'warning.main', ml: '6px' },
                              '&:hover': onAlertClick ? { backgroundColor: 'rgba(237, 108, 2, 0.16)' } : undefined,
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
                <Table size="small" aria-label="disease-details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Descriptions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <Box px={2} my={0.5}>
                        {row.descriptions?.trim().length ? row.descriptions : 'Empty'}
                      </Box>
                    </TableRow>
                  </TableBody>
                </Table>
                <Table size="small" aria-label="not-distributed-countries" sx={{ mt: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Not distributed countries: {row.notDistributedCountryCodes.length}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <Box px={2} my={0.5} display="flex" flexWrap={'wrap'}>
                        {row.notDistributedCountryCodes.length > 0 ? (
                          row.notDistributedCountryCodes.map((country) => (
                            <Tooltip key={country.code} title={country.name}>
                              <Box
                                component="span"
                                p={0.5}
                                m={0.25}
                                sx={{
                                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                  borderRadius: '4px',
                                }}
                              >
                                {country.name}
                              </Box>
                            </Tooltip>
                          ))
                        ) : (
                          <Box sx={{ color: 'rgba(116, 116, 116, 1)' }}>All countries covered</Box>
                        )}
                      </Box>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
